/**
 * AI Router Types
 *
 * Provider hierarchy:
 * 1. Groq (primary) - Fast & cheap, handles trivial/simple/moderate tasks
 * 2. OpenAI (secondary) - For complex tasks requiring GPT-4
 * 3. Anthropic (expert) - For expert/critical tasks requiring Claude
 */

// Available AI providers
export type AIProvider = 'groq' | 'openai' | 'anthropic';

// Available models per provider
export type GroqModel =
  | 'llama-3.1-8b-instant'                          // Fast (560 T/s), 131K context
  | 'llama-3.3-70b-versatile'                       // Best quality (280 T/s), 131K context
  | 'meta-llama/llama-4-scout-17b-16e-instruct'     // Vision + multimodal
  | 'meta-llama/llama-4-maverick-17b-128e-instruct' // Vision + multimodal
  | 'qwen/qwen3-32b'                                // Good for reasoning
  | 'gemma2-9b-it';                                 // Google's model

export type OpenAIModel = 'gpt-3.5-turbo' | 'gpt-4' | 'gpt-4-turbo' | 'gpt-4o' | 'gpt-4o-mini';
export type AnthropicModel = 'claude-3-haiku-20240307' | 'claude-3-5-sonnet-20241022' | 'claude-3-opus-20240229';

export type AIModel = GroqModel | OpenAIModel | AnthropicModel;

// Complexity levels
export type ComplexityLevel = 'trivial' | 'simple' | 'moderate' | 'complex' | 'expert';

// Message analysis result
export interface MessageAnalysis {
  // Raw metrics
  wordCount: number;
  charCount: number;
  sentenceCount: number;
  questionCount: number;
  hasCode: boolean;
  hasNumbers: boolean;
  hasTechnicalTerms: boolean;
  hasMultipleTopics: boolean;
  estimatedTokens: number;

  // Detected intent
  intent: MessageIntent;

  // Complexity assessment
  complexity: ComplexityLevel;
  complexityScore: number; // 0-100
  complexityFactors: string[];
}

// Message intent categories
export interface MessageIntent {
  category: 'greeting' | 'question' | 'command' | 'conversation' | 'analysis' | 'creation' | 'unknown';
  domain: 'general' | 'technical' | 'business' | 'creative' | 'data' | 'code';
  requiresContext: boolean;
  requiresTools: boolean;
  estimatedResponseLength: 'short' | 'medium' | 'long';
}

// Routing decision
export interface RoutingDecision {
  provider: AIProvider;
  model: AIModel;
  fallbackProvider?: AIProvider;
  fallbackModel?: AIModel;
  reasoning: string[];
  confidence: number; // 0-1
  estimatedCost: 'free' | 'low' | 'medium' | 'high';
  estimatedLatency: 'fast' | 'medium' | 'slow';
}

// Complete routing result
export interface AIRoutingResult {
  messageId: string;
  timestamp: Date;
  originalMessage: string;
  analysis: MessageAnalysis;
  decision: RoutingDecision;
  pipelineSteps: PipelineStep[];
}

// Pipeline step for logging
export interface PipelineStep {
  step: number;
  name: string;
  description: string;
  input: any;
  output: any;
  duration: number; // ms
  decision?: string;
}

// Router configuration
export interface AIRouterConfig {
  // Provider availability
  providers: {
    groq: {
      enabled: boolean;
      apiKey?: string;
      models: GroqModel[];
      maxTokens: number;
    };
    openai: {
      enabled: boolean;
      apiKey?: string;
      models: OpenAIModel[];
      maxTokens: number;
    };
    anthropic: {
      enabled: boolean;
      apiKey?: string;
      models: AnthropicModel[];
      maxTokens: number;
    };
  };

  // Routing preferences
  preferences: {
    costSensitive: boolean; // Minimize API costs
    qualityFirst: boolean; // Prefer better models regardless of cost
    maxComplexityForGroq: ComplexityLevel; // Max complexity for Groq (default: moderate)
  };

  // Logging
  logging: {
    enabled: boolean;
    verbose: boolean;
    logToConsole: boolean;
    logToServer: boolean;
  };
}

// Default configuration
export const DEFAULT_ROUTER_CONFIG: AIRouterConfig = {
  providers: {
    groq: {
      enabled: true,
      models: ['llama-3.1-8b-instant', 'llama-3.1-70b-versatile', 'llama-3.3-70b-versatile'],
      maxTokens: 8192,
    },
    openai: {
      enabled: true, // Used for complex tasks
      models: ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo'],
      maxTokens: 8192,
    },
    anthropic: {
      enabled: true, // Used for expert tasks
      models: ['claude-3-5-sonnet-20241022', 'claude-3-opus-20240229'],
      maxTokens: 8192,
    },
  },
  preferences: {
    costSensitive: true,
    qualityFirst: false,
    maxComplexityForGroq: 'moderate', // Groq handles up to moderate, then escalates
  },
  logging: {
    enabled: true,
    verbose: true,
    logToConsole: true,
    logToServer: false,
  },
};
